/**
 * Paywall copy, as data (platform-agnostic).
 *
 * The sibling of planPresentation.js: that module owns *which* plan gets which
 * badge / default / discount; this one owns the *words*. Before this, every
 * fixed paywall string lived per-surface and drifted — savings read
 * "vs monthly" on web but "vs paying monthly" on mobile, the CTA was
 * "Continue — $X · Quarterly" on web but "Subscribe — $X / quarterly" on
 * mobile, and the banner differed between iPhone and iPad. Web, iPhone and iPad
 * now render these verbatim, so the copy (and its period words / separator)
 * cannot diverge.
 *
 * Mirrors packages/core/src/articles/meterMessages.js: pure builders that take
 * already-derived values (a buildPlanCard result, a formatted price string, a
 * canonical period) and return the final string. Prices and percentages are
 * NEVER computed here — callers pass formatted money and the discount pct from
 * buildPlanCard, so this module holds no numbers.
 *
 * The only brand-varying string — the headline accent line — is NOT stored
 * here: it lives in each brand's shared config (configs/_shared/<brand>) and is
 * passed through resolveHeadline, which supplies a sane fallback so no brand
 * ever renders an empty headline.
 */

import { toCanonicalPeriod } from '../analytics/subscriptionEvents';
import { PLAN_BADGE } from './planPresentation';

/** The one separator used across the whole paywall. */
export const SEPARATOR = '·';

/** Mobile banner title — one string for iPhone and iPad (they used to differ). */
export const BANNER_TITLE = 'Unlock Premium Content';

/** Shown under the promoted plan only; a plain reason, not a badge. */
export const PROMOTED_RATIONALE = 'Best balance of savings and flexibility';

/**
 * Headline fallback. line1 is the same for every brand; line2 is the accent
 * line and is normally supplied per-brand — this generic value only shows when
 * a brand has not set one (e.g. a not-yet-configured or white-label brand),
 * never an empty string.
 */
const HEADLINE_FALLBACK = { line1: 'Everything You Need to', line2: 'Pass Your Exam' };

/**
 * Resolve the two-line headline from a brand's config, filling blanks.
 *
 * @param {{line1?: string, line2?: string}} [headline] brand config's PAYWALL_COPY.headline
 * @returns {{line1: string, line2: string}} never-empty lines
 */
export const resolveHeadline = (headline) => ({
  line1: String(headline?.line1 ?? '').trim() || HEADLINE_FALLBACK.line1,
  line2: String(headline?.line2 ?? '').trim() || HEADLINE_FALLBACK.line2,
});

/** Badge id -> display label. Core owns which plan gets a badge; this is its copy. */
const BADGE_LABELS = {
  [PLAN_BADGE.MOST_POPULAR]: 'MOST POPULAR',
  [PLAN_BADGE.BEST_VALUE]: 'BEST VALUE',
};

/**
 * Label for a badge id (from getPlanBadges / buildPlanCard.badge).
 * @param {string|null|undefined} badge PLAN_BADGE value
 * @returns {string|null} label, or null for no/unknown badge
 */
export const getBadgeLabel = (badge) => BADGE_LABELS[badge] ?? null;

/**
 * Renewal-cadence phrase for a plan's subtext (the part after the per-month
 * equivalent): "billed monthly" / "every 3 months" / "billed annually".
 *
 * @param {string} period period label in any dialect
 * @returns {string} cadence phrase, '' for unknown/one-time
 */
export const getBillingLabel = (period) => {
  switch (toCanonicalPeriod(period)) {
    case 'monthly': return 'billed monthly';
    case 'quarterly': return 'every 3 months';
    case 'semiannual': return 'every 6 months';
    case 'annual': return 'billed annually';
    case 'weekly': return 'billed weekly';
    default: return '';
  }
};

/**
 * A plan card's subtext line: "{perMonth}/mo · {billing}".
 *
 * @param {string} period canonical-or-dialect period label
 * @param {string|null} perMonthText pre-formatted per-month money (e.g. "$16.66"); null when unavailable
 * @returns {string} the subtext (just the cadence when no per-month rate)
 */
export const getPlanSubtext = (period, perMonthText) => {
  const billing = getBillingLabel(period);
  if (!perMonthText) return billing;
  const perMo = `${perMonthText}/mo`;
  return billing ? `${perMo} ${SEPARATOR} ${billing}` : perMo;
};

/**
 * Savings line: "Save {pct}% vs monthly". Withheld below 1% and for a null pct
 * (monthly's own baseline), so a card never shows "Save 0%".
 *
 * @param {number|null|undefined} discountPct whole percent from buildPlanCard.discountPct
 * @returns {string|null}
 */
export const getSavingsLine = (discountPct) => {
  if (typeof discountPct !== 'number' || !Number.isFinite(discountPct) || discountPct < 1) return null;
  return `Save ${Math.round(discountPct)}% vs monthly`;
};

/** CTA verb per payment mode. One-time is web-only. */
const CTA_VERB = { subscription: 'Subscribe', one_time: 'Buy' };

/**
 * CTA button label: "Subscribe — {price}" (subscription) or "Buy — {price}"
 * (one-time); bare verb until a price is known (e.g. while offerings load).
 * "Subscribe" must never render in one-time mode.
 *
 * @param {string|null|undefined} priceText pre-formatted price (e.g. "$49.99")
 * @param {'subscription'|'one_time'} [mode]
 * @returns {string}
 */
export const getCtaLabel = (priceText, mode = 'subscription') => {
  const verb = CTA_VERB[mode] ?? CTA_VERB.subscription;
  return priceText ? `${verb} — ${priceText}` : verb;
};

/** Per-month equivalent label for the price column: "{x}/mo". */
export const getPerMonthLabel = (perMonthText) => (perMonthText ? `${perMonthText}/mo` : '');

/** Payment-mode hint under the toggle tab (web-only toggle). */
export const getModeHint = (mode) =>
  mode === 'one_time' ? `No commitment ${SEPARATOR} no renewal` : `Renews ${SEPARATOR} cancel anytime`;

/**
 * How a term reads in running text under the button: "per month" /
 * "every 3 months" / "per year".
 *
 * @param {string} period period label in any dialect
 * @returns {string} '' for unknown/one-time
 */
export const getPeriodPhrase = (period) => {
  switch (toCanonicalPeriod(period)) {
    case 'weekly': return 'per week';
    case 'monthly': return 'per month';
    case 'quarterly': return 'every 3 months';
    case 'semiannual': return 'every 6 months';
    case 'annual': return 'per year';
    default: return '';
  }
};

/**
 * How a one-time term reads as a duration of access: "for 3 months" / "for 1
 * year" — never a renewal cadence ("per month"), which contradicts one-time.
 *
 * @param {string} period period label in any dialect
 * @returns {string} '' for unknown
 */
export const getTermPhrase = (period) => {
  switch (toCanonicalPeriod(period)) {
    case 'weekly': return 'for 1 week';
    case 'monthly': return 'for 1 month';
    case 'quarterly': return 'for 3 months';
    case 'semiannual': return 'for 6 months';
    case 'annual': return 'for 1 year';
    default: return '';
  }
};

/**
 * The reassurance line directly under the CTA. Subscription:
 * "{price} {periodPhrase} · Immediate access · Cancel anytime". One-time:
 * "{price} {termPhrase} · Immediate access · No renewal".
 *
 * @param {string|null|undefined} priceText pre-formatted price
 * @param {string} period period label in any dialect
 * @param {'subscription'|'one_time'} [mode]
 * @returns {string}
 */
export const getCtaSupportLine = (priceText, period, mode = 'subscription') => {
  const oneTime = mode === 'one_time';
  const phrase = oneTime ? getTermPhrase(period) : getPeriodPhrase(period);
  const tail = oneTime ? 'No renewal' : 'Cancel anytime';
  const lead = [priceText, phrase].filter(Boolean).join(' ');
  return [lead, 'Immediate access', tail].filter(Boolean).join(` ${SEPARATOR} `);
};

/**
 * Renewal / billing disclosure near the CTA, per billing channel. The App Store
 * and Google Play texts describe store-managed subscriptions; the Stripe text
 * describes web billing managed from the account's billing portal. One shared
 * string would be factually wrong on at least one platform.
 *
 * All three are legal copy pending review — see the // LEGAL-REVIEW markers.
 *
 * @param {'app_store'|'play_store'|'stripe'} [channel]
 * @returns {string}
 */
const RENEWAL_DISCLOSURE = {
  // LEGAL-REVIEW: reused verbatim from the shipping mobile app (iOS / StoreKit).
  app_store:
    'Subscriptions will be charged to your credit card through your app store account. Your subscription will automatically renew unless canceled at least 24 hours before the end of current period. You will be able to cancel the subscription once activated. Manage your subscriptions in Account Settings after purchase.',
  // LEGAL-REVIEW: Android / Google Play wording (new — we ship Android).
  play_store:
    'Subscriptions are billed through your Google Play account and automatically renew unless canceled at least 24 hours before the end of the current period. You can manage or cancel your subscription anytime in the Google Play Store after purchase.',
  // LEGAL-REVIEW: web / Stripe wording (new).
  stripe:
    'Your subscription is billed to your payment method and automatically renews at the end of each period until you cancel. You can manage or cancel anytime from your Profile using Manage subscription; access continues through the end of the paid period.',
};
export const getRenewalDisclosure = (channel = 'stripe') =>
  RENEWAL_DISCLOSURE[channel] ?? RENEWAL_DISCLOSURE.stripe;

/**
 * Expiry disclosure for a one-time (non-recurring) purchase — web only.
 * LEGAL-REVIEW: new draft.
 *
 * @returns {string}
 */
export const getExpiryDisclosure = () =>
  'This is a one-time purchase, not a subscription. Access is granted immediately and ends when the purchased term expires. It does not auto-renew and you will not be charged again.';

/** Lead line for the "How to cancel" dialog. */
export const getCancelIntro = () => 'Cancelling is easy — just follow these steps:';

/**
 * Cancellation steps for the user's OWN store, so the dialog shows one platform
 * instead of both. Returned as ordered steps (+ the platform's help URL) so the
 * UI can render a real list, not a centered text blob.
 *
 * @param {'app_store'|'play_store'|'stripe'} [channel]
 * @returns {{steps: string[], supportUrl: string|null}}
 */
const CANCEL_INFO = {
  app_store: {
    steps: [
      'Open the Settings app',
      'Tap your name at the top',
      'Tap Subscriptions',
      'Tap this subscription',
      'Tap Cancel Subscription',
    ],
    supportUrl: 'https://support.apple.com/en-us/118428',
  },
  play_store: {
    steps: [
      'Open the Google Play Store app',
      'Check you are signed in to the correct Google account',
      'Tap your profile icon, then Payments & subscriptions → Subscriptions',
      'Select this subscription',
      'Tap Cancel subscription, then confirm',
    ],
    supportUrl: 'https://support.google.com/googleplay/answer/7018481',
  },
  // Web / Stripe: managed from the Profile (the "Manage subscription" link opens
  // the secure billing portal), available after you subscribe. No external help
  // URL — the steps are complete.
  stripe: {
    steps: [
      'Sign in and open your Profile',
      'Click Manage subscription to open the secure billing portal',
      'Select Cancel plan, then confirm',
      'Your access continues until the end of the paid period',
    ],
    supportUrl: null,
  },
};
export const getCancelInfo = (channel = 'app_store') => CANCEL_INFO[channel] ?? CANCEL_INFO.app_store;
